import React, {useEffect, useMemo, useState} from 'react';
import Translate, {t} from "../../../../../../../components/Translate";
import DropdownSelect from "../../../../../../../components-new/form/DropdownSelect";
import BotAvatar from "../../../clients/new/overview/BotAvatar";
import DonutChart from "../../../../../../../components-new/general/charts/DonutChart";
import BarsChart from "../../../../../../../components-new/general/charts/BarsChart";
import useChartPalette from "../../../../../../../components-new/general/charts/palette";
import config from "../../../../../../../config";
import RatingStars from "../../../../../../../components-new/general/RatingStars";
import Tooltip from "../../../../../../../components-new/general/Tooltip";
import {getStatisticBotNumbers} from "../../../../../../../services/statistics/api";
import StatisticCard from "../../../../../../../components-new/general/statistics/StatisticCard";
import StatFilterCard from "../../../../../../../components-new/general/statistics/StatFilterCard";
import FrownIcon from "../../../../../../../assets/icons/FrownIcon";
import Colors from "../../../../../../../utils/Colors";
import SmileIcon from "../../../../../../../assets/icons/SmileIcon";
import ForwardIcon from "../../../../../../../assets/icons/ForwardIcon";
import CheckIcon from "../../../../../../../assets/icons/CheckIcon";
import ClockArrowDownIcon from "../../../../../../../assets/icons/ClockArrowDownIcon";

function BotRequestsStatistics({company, companyInfoList, user, openTrModal, tariff}) {
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [countData, setCountData] = useState({});
    const [isFetching, setIsFetching] = useState(false);

    const [selectedAiCompanies, setSelectedAiCompanies] = useState([]);
    const [selectedNotRelevantCompanies, setSelectedNotRelevantCompanies] = useState([]);

    /*
     * The charts choose their own bots.
     *
     * They could have read the cards' figures instead, but the cards are three
     * separate selections over three different sets - one of them only the
     * smart companies - and a chart that silently follows whichever card was
     * touched last is a chart nobody can trust. One selector, both charts, and
     * it asks the server for its own numbers.
     */
    const [chartCompanies, setChartCompanies] = useState([]);
    const [chartData, setChartData] = useState({});
    const [loadingChart, setLoadingChart] = useState(false);

    const [aiRatingData, setAiRatingData] = useState({});
    const [loadingAi, setLoadingAi] = useState(false);

    const features = tariff ? tariff.features : []

    const smartCompanies = useMemo(function () {
        if (!companyInfoList) return [];
        return companyInfoList.filter(function (c) {
            return c.companyType === "SMART";
        });
    }, [companyInfoList]);

    const smartFilterItems = useMemo(function () {
        return smartCompanies.map(function (c) {
            return {id: c.companyId, name: c.companyName};
        });
    }, [smartCompanies]);

    const toggleAiCompany = function (id) {
        setSelectedAiCompanies(function (prev) {
            if (prev.includes(id)) {
                return prev.filter(function (x) {
                    return x !== id;
                });
            }
            return prev.concat([id]);
        });
    };

    const toggleNotRelevantCompany = function (id) {
        setSelectedNotRelevantCompanies(function (prev) {
            if (prev.includes(id)) {
                return prev.filter(function (x) {
                    return x !== id;
                });
            }
            return prev.concat([id]);
        });
    };

    const companyIds = useMemo(() => {
        if (selectedCompanyId !== '') return [selectedCompanyId];
        return (company && company.subs ? company.subs.map(function (sub) {
            return sub.id;
        }) : []);
    }, [selectedCompanyId, company]);

    useEffect(function () {
        var ids = smartCompanies.map(function (c) {
            return c.companyId;
        });

        setSelectedAiCompanies(ids);
        setSelectedNotRelevantCompanies(ids);
    }, [smartCompanies]);

    useEffect(function () {
        if (!selectedAiCompanies || selectedAiCompanies.length === 0) {
            setAiRatingData({
                positiveRatingRequests: 0,
                negativeRatingRequests: 0,
                ratingScore1Requests: 0,
                ratingScore2Requests: 0,
                ratingScore3Requests: 0,
                ratingScore4Requests: 0,
                ratingScore5Requests: 0,
            });
            return;
        }

        var active = true;
        setLoadingAi(true);

        getStatisticBotNumbers(selectedAiCompanies).then(function (data) {
            if (active) setAiRatingData(data);
        }).finally(function () {
            setLoadingAi(false);
        });

        return function () {
            active = false;
        };
    }, [selectedAiCompanies]);

    useEffect(() => {
        var active = true;
        setIsFetching(true);
        getStatisticBotNumbers(companyIds).then(function (data) {
            if (active) setCountData(data);
        }).finally(() => {
            setIsFetching(false);
        });
        return function () {
            active = false;
        };
    }, [companyIds]);

    /* every bot to begin with, which is what the cards above already show */
    useEffect(() => {
        setChartCompanies(company && company.subs ? company.subs.map(sub => sub.id) : []);
    }, [company]);

    useEffect(() => {
        if (chartCompanies.length === 0) {
            setChartData({});
            return undefined;
        }

        var active = true;
        setLoadingChart(true);

        getStatisticBotNumbers(chartCompanies).then(function (data) {
            if (active) setChartData(data || {});
        }).finally(function () {
            if (active) setLoadingChart(false);
        });

        return function () {
            active = false;
        };
    }, [chartCompanies]);

    const chartBotOptions = useMemo(
        () => (company && company.subs ? company.subs : []).map(sub => ({
            value: sub.id,
            label: sub.name,
            bot: sub
        })),
        [company]
    );

    const selectedChartBots = useMemo(
        () => chartBotOptions.filter(option => chartCompanies.includes(option.value)),
        [chartBotOptions, chartCompanies]
    );

    const toggleChartBot = (id) => {
        setChartCompanies(prev => prev.includes(id)
            ? prev.filter(x => x !== id)
            : prev.concat([id]));
    };

    const toggleAllChartBots = () => {
        const all = chartBotOptions.length > 0 && selectedChartBots.length === chartBotOptions.length;
        setChartCompanies(all ? [] : chartBotOptions.map(o => o.value));
    };

    const filteredList = useMemo(() => {
        if (!companyInfoList) return [];
        if (selectedCompanyId) {
            return companyInfoList.filter(function (item) {
                return item.companyId === selectedCompanyId;
            });
        }
        return companyInfoList;
    }, [selectedCompanyId, companyInfoList]);

    const aiNotRelevantQuestionCount = useMemo(function () {
        var sum = 0;

        for (var i = 0; i < smartCompanies.length; i++) {
            var item = smartCompanies[i];
            if (selectedNotRelevantCompanies.includes(item.companyId)) {
                sum += item.aiNotRelevantQuestionCount ? item.aiNotRelevantQuestionCount : 0;
            }
        }

        return sum;
    }, [smartCompanies, selectedNotRelevantCompanies]);

    const ratingValues = useMemo(function () {
        var rating1 = aiRatingData.ratingScore1Requests || 0;
        var rating2 = aiRatingData.ratingScore2Requests || 0;
        var rating3 = aiRatingData.ratingScore3Requests || 0;
        var rating4 = aiRatingData.ratingScore4Requests || 0;
        var rating5 = aiRatingData.ratingScore5Requests || 0;
        var total = rating1 + rating2 + rating3 + rating4 + rating5;
        var score = rating1 + (rating2 * 2) + (rating3 * 3) + (rating4 * 4) + (rating5 * 5);
        var percent = total ? Math.round((score * 20) / total) : 0;
        var stars = (percent * 5) / 100;

        return {
            ratingTotal: total,
            ratingPercent: percent,
            ratingInStars: stars
        };
    }, [aiRatingData]);

    const hasAI = (
        tariff &&
        tariff.features &&
        tariff.features.indexOf(config.features.AI) !== -1
    );

    const palette = useChartPalette();

    /*
     * How bot conversations ended.
     *
     * Solved, expired and handed to an operator are the three exits, and every
     * conversation leaves by one of them - which is what makes a ring the right
     * shape for it. The rated counts are deliberately not in here: a rating is
     * something that happened to a conversation, not how it finished, and a
     * conversation can be both solved and badly rated.
     */
    const outcomeSlices = useMemo(() => [
        {
            key: 'solved',
            name: t({text: 'solvedByBotCount', user}),
            value: chartData.solvedByBotRequests || 0,
            color: palette.positive,
        },
        {
            key: 'transferred',
            name: t({text: 'transferredToOperatorsCount', user}),
            value: chartData.transferredToOperatorRequests || 0,
            color: palette.series1,
        },
        {
            key: 'expired',
            name: t({text: 'expiredCount', user}),
            value: chartData.expiredRequests || 0,
            color: palette.warning,
        },
    ], [chartData, palette, user]);

    /*
     * The five marks, left to right.
     *
     * The rating card beside this one averages them into a single percentage,
     * which hides the shape: eighty percent is the same number whether everyone
     * said four or half said five and half said three, and those are two very
     * different bots.
     */
    const ratingBars = useMemo(() => {
        const colors = [palette.negative, palette.negative, palette.warning, palette.positive, palette.positive];

        return [1, 2, 3, 4, 5].map((score, index) => ({
            name: String(score),
            score: score,
            count: chartData['ratingScore' + score + 'Requests'] || 0,
            color: colors[index],
        }));
    }, [chartData, palette]);

    const counterCards = [
        <StatisticCard
            key="all"
            onClick={() => openTrModal('botsAllRequests')}
            title={'all'}
            value={countData.allRequests}
            loading={isFetching}
        />,
        <StatisticCard
            key="solved"
            icon={<CheckIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
            onClick={() => openTrModal('solvedBotsRequests', {expired: false, transferredToOperator: false})}
            title={'solvedByBotCount'}
            value={countData.solvedByBotRequests}
            loading={isFetching}
        />,
        features.includes(config.features.EXPIRATION_TIME) && (
            <StatisticCard
                key="expired"
                icon={<ClockArrowDownIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
                onClick={() => openTrModal('expiredBotsRequests', {expired: true, transferredToOperator: false})}
                title={'expiredCount'}
                value={countData.expiredRequests}
                loading={isFetching}
            />
        ),
        <StatisticCard
            key="transferred"
            icon={<ForwardIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
            onClick={() => openTrModal('transferredToOperatorBotsRequests', {
                expired: false,
                transferredToOperator: true
            })}
            title={'transferredToOperatorsCount'}
            value={countData.transferredToOperatorRequests}
            loading={isFetching}
        />,
        hasAI && (
            <StatisticCard
                key="goodRated"
                icon={<SmileIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
                onClick={() => openTrModal('ratedBotsRequests', {ratingScores: [4, 5]})}
                title={'goodRated'}
                value={countData.positiveRatingRequests}
                loading={isFetching}
            />
        ),
        hasAI && (
            <StatisticCard
                key="badRated"
                icon={<FrownIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
                onClick={() => openTrModal('ratedBotsRequests', {ratingScores: [1, 2]})}
                title={'badRated'}
                value={countData.negativeRatingRequests}
                loading={isFetching}
            />
        ),
    ].filter(Boolean);

    const filterCards = hasAI ? [
        <StatFilterCard
            key="aiRating"
            title={'aiRating'}
            items={smartFilterItems}
            selectedIds={selectedAiCompanies}
            onToggle={toggleAiCompany}
            onToggleAll={setSelectedAiCompanies}
            loading={loadingAi}
        >
            <div className="stat-rating">
                <span className={'stat-tile__value' + (loadingAi ? ' stat-tile__value--stale' : '')}>
                    <Tooltip text={'Rating'}>{ratingValues.ratingPercent}</Tooltip>
                    <span className="stat-rating__separator">/</span>
                    <Tooltip text={'ratingCount'}>{ratingValues.ratingTotal}</Tooltip>
                </span>
                <RatingStars value={ratingValues.ratingInStars} starSize="small"/>
            </div>
        </StatFilterCard>,
        <StatFilterCard
            key="notRelevant"
            title={'notRelevantToAICount'}
            items={smartFilterItems}
            selectedIds={selectedNotRelevantCompanies}
            onToggle={toggleNotRelevantCompany}
            onToggleAll={setSelectedNotRelevantCompanies}
            loading={isFetching}
            value={aiNotRelevantQuestionCount}
        />,
    ] : [];

    return (
        <React.Fragment>
            <div className="stat-card-group stat-card-group--counters">
                {counterCards}
            </div>
            {filterCards.length > 0 && (
                <div className="stat-card-group stat-card-group--filters">
                    {filterCards}
                </div>
            )}

            <div className="stat-section">
                {/* the only thing in the head, so it sits on the right rather
                    than alone on the left where a missing title would show */}
                <div className="stat-section__head" style={{justifyContent: 'flex-end'}}>
                    <div className="stat-section__filters">
                        <DropdownSelect
                            selectedValue={selectedChartBots}
                            options={chartBotOptions}
                            onChange={(option) => toggleChartBot(option.value)}
                            onSelectAll={toggleAllChartBots}
                            renderLabel={(selected) => {
                                if (!selected || selected.length === 0) return <Translate text="notSelected"/>;
                                if (selected.length === chartBotOptions.length) return <Translate text="allBots"/>;
                                if (selected.length === 1) return selected[0].label;
                                return selected.length + ' / ' + chartBotOptions.length;
                            }}
                            searchBy={chartBotOptions.length > 7
                                ? (option, search) => option.label.toLowerCase().includes(search.toLowerCase())
                                : null}
                            variant={'outline'}
                            styles={{width: '260px'}}
                        >
                            {option => (
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0}}>
                                    <BotAvatar client={option.bot} size={26} imgSize={24}/>
                                    <span style={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    }}>{option.label}</span>
                                </div>
                            )}
                        </DropdownSelect>
                    </div>
                </div>

                <div className="chart-row" style={{marginBottom: 0, opacity: loadingChart ? 0.55 : 1}}>
                    <DonutChart
                        titleKey="botOutcomes"
                        totalLabelKey="all"
                        data={outcomeSlices}
                        onSliceClick={(slice) => {
                            if (slice.key === 'solved') {
                                openTrModal('solvedBotsRequests', {expired: false, transferredToOperator: false});
                            } else if (slice.key === 'transferred') {
                                openTrModal('transferredToOperatorBotsRequests', {
                                    expired: false,
                                    transferredToOperator: true
                                });
                            } else {
                                openTrModal('expiredBotsRequests', {expired: true, transferredToOperator: false});
                            }
                        }}
                    />

                    {hasAI && (
                        <BarsChart
                            titleKey="botRatingDistribution"
                            hintKey="botRatingDistributionHint"
                            data={ratingBars}
                            series={[{key: 'count', name: t({text: 'ratingCount', user})}]}
                            onBarClick={(bar) => openTrModal('ratedBotsRequests', {ratingScores: [bar.score]})}
                        />
                    )}
                </div>
            </div>
        </React.Fragment>
    );
}

export default BotRequestsStatistics;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/requestsToBots/BotRequestsStatistics.jsx